<template>
  <div>
    <CashbackBottomSheet :name="CASHBACK_BOTTOMSHEET" />
    <section class="space-y-5">
      <CitySelect
        :bottom-sheet-name="CITY_SELECT_BOTTOMSHEET"
        :is-loading="isCountriesLoading"
        only-kz-cities
        @onCitySelect="handleCitySelect"
      >
        <template #trigger>
          <i18n-t
            v-if="origCity"
            class="text-text-neutral-primary flex items-center space-x-2 text-base font-medium"
            keypath="hotOffers.v2.title"
            tag="span"
          >
            <template #from>
              <span class="ml-2 text-sm font-normal">
                {{ $t('hotOffers.v2.departureFrom', { city: origCity.name }) }}
              </span>
            </template>
          </i18n-t>
          <span v-else class="text-text-neutral-primary text-base font-medium">
            {{ $t('hotOffers.selectCity') }}
          </span>
        </template>
      </CitySelect>
      <template v-if="isCountriesLoading">
        <OffersCarouselShimmer v-for="i in 2" :key="i" /> <slot />
      </template>
      <template v-else-if="isHotCitiesOffersEmpty">
        <EmptyResults
          :title="$t('hotOffers.v2.emptyResults.title')"
          :description="$t('hotOffers.v2.emptyResults.description')"
        >
          <template #action>
            <button
              type="button"
              class="text-button-primary-default text-base font-semibold"
              @click="openCitySelectBottomSheet"
            >
              {{ $t('hotOffers.v2.emptyResults.action') }}
            </button>
          </template>
        </EmptyResults>
        <slot />
      </template>
      <template v-else>
        <OffersCarouselBlock
          v-for="(result, i) in hotCitiesResults"
          :key="i"
          :offers="result.offers"
          :is-loading="result.isLoading"
          :source="`hotOffers.${i}`"
          :cashback-click-source="
            analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource
              .HOT_OFFERS
          "
          @onCashbackClick="bottomSheet.show(CASHBACK_BOTTOMSHEET)"
        >
          <CarouselTitle
            v-if="result.country"
            :country="result.country"
            @onShowAllOffers="goToHotOffersPage"
          />
        </OffersCarouselBlock>
        <slot />
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { CashbackBottomSheet, EmptyResults } from 'freedom-shared';
  import { controllerModel } from 'shared';

  import {
    OffersCarouselBlock,
    OffersCarouselShimmer,
    CarouselTitle,
  } from '@/features/offers';

  import { hotOffersApi } from '@/entities/hot-offers';
  import { CitySelect } from '@/entities/city-select';
  import type { hotOffersTypes } from '@/entities/hot-offers';

  import { analyticsEventsLib, getCountryData } from '@/shared/lib';

  import { useHotOffers } from '../model';

  const router = useRouter();

  const CITY_SELECT_BOTTOMSHEET = 'hot-offers-city-select-bottom-sheet-name';
  const CASHBACK_BOTTOMSHEET = 'cashback-bottom-sheet';
  const bottomSheet = controllerModel.useBottomSheet();

  const {
    origCityCode,
    countryCodeList,
    origCity,
    isCountriesLoading,
    changeDestCountryCode,
    changeOrigCityCode,
  } = useHotOffers();

  const allCitiesQuery = hotOffersApi.useAllHotCitiesQuery({
    origCityCode,
    countryCodeList,
  });

  const hotCitiesResults = computed(() => {
    return allCitiesQuery.value.map(query => {
      return {
        offers: query.results || [],
        country:
          query.results &&
          query.results.length > 0 &&
          getCountryData(query.results[0]),
        isLoading: query.isLoading,
      };
    });
  });
  const isHotCitiesOffersEmpty = computed(() => {
    return (
      hotCitiesResults.value.length === 0 ||
      hotCitiesResults.value.every(result => result.offers.length === 0)
    );
  });

  const goToHotOffersPage = (destCountryCode: string) => {
    changeDestCountryCode(destCountryCode);
    router.push({
      name: 'HotOffers',
      params: {
        origCity: origCity.value.code,
        destCountry: destCountryCode,
      },
    });
  };

  const handleCitySelect = (city: hotOffersTypes.MappedCity) => {
    analyticsEventsLib.hotOffersOriginCityChange({
      origCityCode: city.code,
      source: 'main_page',
    });
    changeOrigCityCode(city.code);
  };

  const openCitySelectBottomSheet = () => {
    bottomSheet.show(CITY_SELECT_BOTTOMSHEET);
  };
</script>
